import { useEffect, useRef, useCallback, useMemo } from 'react'
import { supabase } from '@/shared/utils'
import { useAuth } from '@/modules/auth'

interface UseRealtimeTripMembersProps {
  tripId: string
  onMemberUpdate: () => void
  enabled?: boolean
}

export function useRealtimeTripMembers({
  tripId,
  onMemberUpdate,
  enabled = true
}: UseRealtimeTripMembersProps) {
  const { user } = useAuth()
  const subscriptionRef = useRef<any>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const lastUpdateRef = useRef<number>(Date.now())
  const onMemberUpdateRef = useRef(onMemberUpdate)

  // Keep the ref updated
  useEffect(() => {
    onMemberUpdateRef.current = onMemberUpdate
  }, [onMemberUpdate])

  // Fallback polling mechanism - only start if real-time subscription fails
  const startPolling = useCallback(() => {
    if (pollingRef.current) return
    
    // Only start polling if we don't have a real-time connection
    if (subscriptionRef.current?.state !== 'SUBSCRIBED') {
      pollingRef.current = setInterval(() => {
        onMemberUpdateRef.current()
      }, 30000) // Poll every 30 seconds
    }
  }, [])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!enabled || !tripId || !user) {
      return
    }

    // Subscribe to changes in trip_members table for this specific trip
    const subscription = supabase
      .channel(`trip_members_${tripId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'trip_members',
          filter: `trip_id=eq.${tripId}`
        },
        (payload) => {
          // Show notification for guest joins
          if (payload.eventType === 'INSERT' && payload.new?.member_type === 'guest') {
          }
          
          // Only trigger update if the change is not from the current user
          if ((payload.new as any)?.user_id !== user.id && (payload.old as any)?.user_id !== user.id) {
            onMemberUpdateRef.current()
          }
        }
      )
      .subscribe()

    subscriptionRef.current = subscription

    // Disable polling for now - real-time subscriptions should be sufficient
    // const pollingTimeout = setTimeout(() => {
    //   if (subscriptionRef.current?.state !== 'SUBSCRIBED') {
    //     startPolling()
    //   }
    // }, 5000)

    return () => {
      // clearTimeout(pollingTimeout) // Disabled polling
      stopPolling()
      
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current)
        subscriptionRef.current = null
      }
    }
  }, [tripId, user?.id, enabled])

  return {
    isConnected: subscriptionRef.current?.state === 'SUBSCRIBED'
  }
}
